// Flight clocks are airport-local, never viewer-local. Legacy wall-clock values
// stay wall-clock; only timestamps with an offset can be converted to an IANA zone.
export function flightDateTime(value, timezone, prefs = {}) {
  const raw = String(value || '');
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!match || Number(match[2]) > 23 || Number(match[3]) > 59 || !Number.isFinite(Date.parse(`${match[1]}T12:00:00Z`))) return { date: '', time: '', calendarDate: '', timezoneLabel: 'Timezone unavailable' };
  let calendarDate = match[1], hour = Number(match[2]), minute = match[3];
  let timezoneLabel = 'Local time';
  const offset = raw.match(/(Z|[+-]\d{2}:\d{2})$/);
  if (timezone && offset) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(raw));
      const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
      calendarDate = `${p.year}-${p.month}-${p.day}`; hour = Number(p.hour); minute = p.minute;
      timezoneLabel = timezone;
    } catch { timezoneLabel = 'Timezone unavailable'; }
  } else if (offset) {
    timezoneLabel = offset[1] === 'Z' ? 'UTC · airport timezone unavailable' : `UTC${offset[1]}`;
  } else if (timezone) timezoneLabel = timezone;
  const date = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(`${calendarDate}T12:00:00Z`));
  const euDate = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(`${calendarDate}T12:00:00Z`));
  const time = prefs.time_view_format === '24H' ? `${String(hour).padStart(2, '0')}:${minute}` : `${hour % 12 || 12}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`;
  return { date: prefs.date_input_format === 'EU' ? euDate : date, time, calendarDate, timezoneLabel };
}

function bookedTime(leg, side) {
  const date = leg[`${side}_date`], time = leg[`${side}_time`];
  return leg[`${side}_datetime`] || (/^\d{4}-\d{2}-\d{2}T/.test(String(time || '')) ? time : date && time ? `${String(date).split('T')[0]}T${time}` : /^\d{4}-\d{2}-\d{2}T/.test(String(date || '')) ? date : null);
}

export function hasFlightScheduleConflict(leg = {}) {
  const booked = bookedTime(leg, 'departure') || leg.departure_date;
  const provider = leg.scheduled_departure;
  if (!booked || !provider) return false;
  const bookedDay = /^\d{4}-\d{2}-\d{2}$/.test(booked) ? booked : flightDateTime(booked, leg.departure_timezone).calendarDate;
  const providerDay = flightDateTime(provider, leg.departure_timezone).calendarDate;
  return !!(bookedDay && providerDay && bookedDay !== providerDay);
}

export function flightSide(leg = {}, side, prefs = {}, fallback) {
  const conflict = hasFlightScheduleConflict(leg);
  const booked = bookedTime(leg, side);
  const scheduled = (conflict ? booked : leg[`scheduled_${side}`] || booked) || fallback;
  const actual = !conflict && leg[`actual_${side}`], estimated = !conflict && leg[`estimated_${side}`];
  const timezone = leg[`${side}_timezone`];
  const selectedField = `${actual ? 'actual' : estimated ? 'estimated' : 'scheduled'}_${side}`;
  return { ...flightDateTime(actual || estimated || scheduled, timezone, prefs), conflict,
    basis: conflict ? '' : leg.time_basis?.[selectedField] || leg.field_observations?.[selectedField]?.time_basis || '',
    kind: actual ? 'Actual' : estimated ? 'Estimated' : 'Scheduled', scheduled: flightDateTime(scheduled, timezone, prefs), raw: actual || estimated || scheduled };
}

export function flightDayDifference(departure, arrival) {
  if (!departure.calendarDate || !arrival.calendarDate) return 0;
  return Math.round((Date.parse(`${arrival.calendarDate}T00:00:00Z`) - Date.parse(`${departure.calendarDate}T00:00:00Z`)) / 86400000);
}

export function flightExportDetails(leg, prefs) {
  return ['departure', 'arrival'].map(side => {
    const clock = flightSide(leg, side, prefs);
    const airport = leg[`${side}_airport`] || leg[`${side}_airport_code`] || '';
    return `${side === 'departure' ? 'Dep' : 'Arr'} ${airport}: ${clock.date || 'Date TBD'} ${clock.time || 'Time TBD'} (${clock.kind.toLowerCase()}${clock.basis ? ` ${clock.basis}` : ''}; ${clock.timezoneLabel})`;
  });
}

// ---------------------------------------------------------------------------
// Flight progress: status and delay from the recorded times. A provider status
// can go stale (a missed event leaves "Scheduled" on a landed flight), so actual
// times outrank it, and a delay can be measured when no native value arrived.
// Mirrors backend/services/flight_lifecycle_service.py.
// ---------------------------------------------------------------------------
const PHASE_RANK = { scheduled: 0, delayed: 0, 'check-in': 1, boarding: 2, 'gate closed': 3, departed: 4, 'en route': 5, landed: 6, arrived: 7 };
const UNCONFIRMED_COMPLETION_GRACE_MS = 12 * 3600000;
// A flight within this many minutes of schedule is on time (industry convention).
export const ON_TIME_TOLERANCE_MINUTES = 15;

export function normalizeFlightStatus(raw) {
  const text = String(raw || '').trim().toLowerCase().replace(/_/g, ' ');
  if (!text || text === 'unknown') return null;
  if (text.includes('possibly') || text.includes('uncertain')) return 'possibly cancelled';
  if (text.startsWith('cancel')) return 'cancelled';
  if (text.includes('divert')) return 'diverted';
  if (text.startsWith('arrived') || text.includes('gate arrival')) return 'arrived';
  if (text.startsWith('landed') || text.startsWith('landing')) return 'landed';
  if (text.startsWith('en route') || text.startsWith('enroute') || ['active', 'in flight', 'airborne', 'approaching'].includes(text)) return 'en route';
  if (text.startsWith('departed') || text.includes('left gate') || text.includes('taxiing')) return 'departed';
  if (text.includes('gate closed') || text === 'gateclosed') return 'gate closed';
  if (text.startsWith('boarding')) return 'boarding';
  if (['check-in', 'checkin', 'check in'].includes(text)) return 'check-in';
  if (text.startsWith('delayed') || (text.startsWith('scheduled') && text.includes('delayed'))) return 'delayed';
  if (['scheduled', 'expected', 'filed', 'on time', 'on-time'].includes(text)) return 'scheduled';
  return text;
}

function instant(value) {
  const raw = String(value || '');
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return null;
  const parsed = Date.parse(/(Z|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`);
  return Number.isFinite(parsed) ? parsed : null;
}

function measuredDelay(leg, side) {
  const scheduled = instant(leg[`scheduled_${side}`]);
  const current = instant(leg[`actual_${side}`]) ?? instant(leg[`estimated_${side}`]);
  return scheduled != null && current != null ? Math.round((current - scheduled) / 60000) : null;
}

function nativeOrMeasured(leg, side) {
  const value = leg[`delay_${side}_minutes`];
  const derived = leg.data_sources?.[`delay_${side}_minutes`] === 'derived';
  if (typeof value === 'number' && Number.isFinite(value) && !derived) return value;
  return measuredDelay(leg, side);
}

export function flightProgress(leg = {}, now = Date.now()) {
  const conflict = hasFlightScheduleConflict(leg);
  const reported = normalizeFlightStatus(leg.flight_status);
  let status = reported;
  if (leg.cancelled) status = 'cancelled';
  else if (leg.diverted) status = 'diverted';
  else if (!conflict && !['cancelled', 'diverted', 'possibly cancelled'].includes(reported)) {
    // A wheels-on time aliased into actual_arrival proves a landing, not a gate arrival.
    const evidence = leg.actual_arrival ? (leg.time_basis?.actual_arrival === 'runway' ? 'landed' : 'arrived')
      : leg.runway_arrival ? 'landed'
      : (leg.actual_departure || leg.runway_departure) ? 'departed' : null;
    if (evidence && (PHASE_RANK[evidence] ?? -1) > (PHASE_RANK[reported] ?? -1)) status = evidence;
  }
  const departureDelay = conflict ? null : nativeOrMeasured(leg, 'departure');
  const arrivalDelay = conflict ? null : nativeOrMeasured(leg, 'arrival');
  const airborne = (PHASE_RANK[status] ?? -1) >= PHASE_RANK.departed;
  // Once the aircraft has left, the arrival delay is what matters.
  const delay = airborne ? (arrivalDelay ?? departureDelay) : (departureDelay ?? arrivalDelay);

  let completed = false; let completedKind = null;
  if (status === 'cancelled') { completed = true; completedKind = 'cancelled'; }
  else if (status === 'arrived') { completed = true; completedKind = 'arrived'; }
  else {
    const arrival = instant(leg.actual_arrival) ?? instant(leg.runway_arrival) ?? instant(leg.estimated_arrival)
      ?? instant(leg.scheduled_arrival) ?? instant(leg.arrival_datetime);
    if (arrival != null && arrival + UNCONFIRMED_COMPLETION_GRACE_MS < now) { completed = true; completedKind = 'unconfirmed'; }
  }
  return { status, departureDelay, arrivalDelay, delay, completed, completedKind };
}

// The leg with a trustworthy status and delays filled in, plus `delay_minutes`
// (the delay that matters for the flight's current phase).
export function withFlightProgress(leg) {
  if (!leg) return leg;
  const progress = flightProgress(leg);
  return {
    ...leg,
    flight_status: progress.status && progress.status !== normalizeFlightStatus(leg.flight_status) ? progress.status : leg.flight_status,
    delay_departure_minutes: progress.departureDelay,
    delay_arrival_minutes: progress.arrivalDelay,
    delay_minutes: progress.delay,
  };
}

export function formatDelayDuration(minutes) {
  const total = Math.abs(Math.round(minutes || 0));
  const hours = Math.floor(total / 60); const mins = total % 60;
  if (!hours) return `${mins}m`;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

export function formatAgo(timestamp, now = Date.now()) {
  const minutes = Math.max(0, Math.floor((now - timestamp) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} days ago`;
}
